<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';

export default {
  name: 'KanbanTasksTab',
  components: { DatePicker },
  props: { item: { type: Object, required: true } },
  data() {
    return {
      modalOpen: false,
      editingTask: null,
      saving: false,
      ptBR: {
        formatLocale: {
          months: [
            'Janeiro',
            'Fevereiro',
            'Março',
            'Abril',
            'Maio',
            'Junho',
            'Julho',
            'Agosto',
            'Setembro',
            'Outubro',
            'Novembro',
            'Dezembro',
          ],
          monthsShort: [
            'Jan',
            'Fev',
            'Mar',
            'Abr',
            'Mai',
            'Jun',
            'Jul',
            'Ago',
            'Set',
            'Out',
            'Nov',
            'Dez',
          ],
          weekdays: [
            'Domingo',
            'Segunda',
            'Terça',
            'Quarta',
            'Quinta',
            'Sexta',
            'Sábado',
          ],
          weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
          weekdaysMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
          firstDayOfWeek: 0,
          firstWeekContainsDate: 1,
        },
        yearFormat: 'YYYY',
        monthBeforeYear: false,
      },
      form: {
        title: '',
        description: '',
        due_at: '',
        priority: 'medium',
        status_key: 'pending',
        assigned_to_user_id: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      allTasks: 'tasks/getTasks',
      uiFlags: 'tasks/getTasksUIFlags',
      agents: 'agents/getAgents',
    }),
    tasks() {
      return (this.allTasks || []).filter(
        t => t.kanban_item_id === this.item.id
      );
    },
    isFetching() {
      return this.uiFlags && this.uiFlags.isFetching;
    },
    accountId() {
      return this.$route.params.accountId;
    },
  },
  watch: {
    'item.id'() {
      this.fetchTasks();
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.fetchTasks();
  },
  methods: {
    fetchTasks() {
      if (!this.item || !this.item.id) return;
      this.$store.dispatch('tasks/fetch', { kanban_item_id: this.item.id });
    },
    openCreate() {
      this.editingTask = null;
      this.form = {
        title: '',
        description: '',
        due_at: '',
        priority: 'medium',
        status_key: 'pending',
        assigned_to_user_id: null,
      };
      this.modalOpen = true;
    },
    openEdit(t) {
      this.editingTask = t;
      this.form = {
        title: t.title || '',
        description: t.description || '',
        due_at: t.due_at ? this.toBrDatetime(t.due_at) : '',
        priority: t.priority || 'medium',
        status_key: t.status_key || 'pending',
        assigned_to_user_id: t.assigned_to_user_id || null,
      };
      this.modalOpen = true;
    },
    closeModal() {
      this.modalOpen = false;
      this.editingTask = null;
    },
    async saveTask() {
      this.saving = true;
      try {
        const payload = Object.assign({}, this.form, {
          kanban_item_id: this.item.id,
        });
        payload.due_at = payload.due_at
          ? this.parseBrDatetime(payload.due_at)
          : null;
        if (this.editingTask) {
          await this.$store.dispatch('tasks/update', {
            id: this.editingTask.id,
            payload,
          });
          useAlert('Tarefa atualizada');
        } else {
          await this.$store.dispatch('tasks/create', payload);
          useAlert('Tarefa criada');
        }
        this.closeModal();
        this.fetchTasks();
      } catch (e) {
        useAlert('Erro ao salvar tarefa');
      } finally {
        this.saving = false;
      }
    },
    async toggleComplete(t) {
      try {
        if (this.isDone(t)) await this.$store.dispatch('tasks/reopen', t.id);
        else await this.$store.dispatch('tasks/complete', t.id);
        this.fetchTasks();
      } catch (e) {
        useAlert('Erro');
      }
    },
    async deleteTask() {
      if (!this.editingTask) return;
      if (!window.confirm('Excluir essa tarefa permanentemente?')) return;
      try {
        await this.$store.dispatch('tasks/remove', this.editingTask.id);
        useAlert('Tarefa excluída');
        this.closeModal();
        this.fetchTasks();
      } catch (e) {
        useAlert('Erro');
      }
    },
    isDone(t) {
      return t.status && t.status.is_terminal;
    },
    statusStyle(t) {
      const c = (t.status && t.status.color) || '#6b7280';
      return { backgroundColor: c + '22', color: c };
    },
    statusLabel(t) {
      return (t.status && t.status.label) || t.status_key;
    },
    priorityColor(p) {
      const map = {
        urgent: 'bg-red-500',
        high: 'bg-orange-400',
        medium: 'bg-blue-400',
        low: 'bg-n-alpha-3',
        none: 'bg-n-alpha-2',
      };
      return map[p] || 'bg-n-alpha-2';
    },
    toBrDatetime(iso) {
      try {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        return (
          pad(d.getDate()) +
          '/' +
          pad(d.getMonth() + 1) +
          '/' +
          d.getFullYear() +
          ' ' +
          pad(d.getHours()) +
          ':' +
          pad(d.getMinutes())
        );
      } catch (e) {
        return '';
      }
    },
    parseBrDatetime(s) {
      const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
      if (!m) return null;
      const [, dd, mm, yyyy, hh, mi] = m;
      return new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(mi)
      ).toISOString();
    },
    formatDate(iso) {
      try {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        const today = new Date();
        const sameDay =
          d.getFullYear() === today.getFullYear() &&
          d.getMonth() === today.getMonth() &&
          d.getDate() === today.getDate();
        if (sameDay)
          return 'Hoje ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
        return (
          pad(d.getDate()) +
          '/' +
          pad(d.getMonth() + 1) +
          ' ' +
          pad(d.getHours()) +
          ':' +
          pad(d.getMinutes())
        );
      } catch (e) {
        return iso;
      }
    },
  },
};
</script>

<template>
  <div class="kanban-tasks-tab">
    <div class="flex items-center justify-between mb-3 px-1">
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">Tarefas</h3>
        <span v-if="tasks.length > 0" class="text-xs text-n-slate-11">{{
          tasks.length
        }}</span>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="text-xs px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
          @click="openCreate"
        >
          + Nova
        </button>
        <router-link
          :to="`/app/accounts/${accountId}/tasks`"
          class="text-xs text-n-slate-11 hover:text-blue-600"
        >
          Ver todas
        </router-link>
      </div>
    </div>

    <div
      v-if="isFetching && tasks.length === 0"
      class="text-center text-n-slate-11 py-4 text-xs"
    >
      Carregando...
    </div>
    <div
      v-else-if="tasks.length === 0"
      class="text-center text-n-slate-10 py-8 text-xs"
    >
      <p>Nenhuma tarefa para este lead</p>
      <button class="mt-2 text-blue-600 hover:underline" @click="openCreate">
        Criar primeira
      </button>
    </div>
    <ul v-else class="space-y-1.5">
      <li
        v-for="t in tasks"
        :key="t.id"
        class="flex items-start gap-2 p-2 rounded-md border border-n-weak hover:bg-n-alpha-2/50 cursor-pointer"
        @click="openEdit(t)"
      >
        <input
          type="checkbox"
          :checked="isDone(t)"
          class="mt-0.5 size-3.5 rounded cursor-pointer"
          @click.stop="toggleComplete(t)"
        />
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-1.5">
            <span
              class="size-1.5 rounded-full inline-block shrink-0"
              :class="[priorityColor(t.priority)]"
            />
            <span
              class="text-xs font-medium truncate"
              :class="[
                isDone(t) ? 'line-through text-n-slate-10' : 'text-n-slate-12',
              ]"
              >{{ t.title }}</span>
          </div>
          <div
            class="flex items-center gap-2 mt-0.5 text-[10px] text-n-slate-11"
          >
            <span
              v-if="t.due_at"
              :class="t.overdue ? 'text-red-500 font-medium' : ''"
              >{{ formatDate(t.due_at) }}</span>
            <span v-if="t.assigned_to">{{ t.assigned_to.name }}</span>
          </div>
        </div>
        <span
          class="text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap"
          :style="statusStyle(t)"
          >{{ statusLabel(t) }}</span>
      </li>
    </ul>

    <woot-modal
      v-if="modalOpen"
      :show="modalOpen"
      :on-close="closeModal"
      :show-close-button="true"
      size="medium"
    >
      <div class="px-6 py-5 border-b border-n-weak">
        <h2 class="text-lg font-extrabold text-n-slate-12">
          {{ editingTask ? 'Editar tarefa' : 'Nova tarefa' }}
        </h2>
      </div>
      <form class="px-6 py-4 space-y-4" @submit.prevent="saveTask">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Título *</label>
          <input
            v-model="form.title"
            type="text"
            required
            class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-2 text-n-slate-12"
            placeholder="O que precisa ser feito?"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Descrição</label>
          <textarea
            v-model="form.description"
            rows="3"
            class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-2 text-n-slate-12"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">Vencimento</label>
            <DatePicker
              v-model:value="form.due_at"
              type="datetime"
              format="DD/MM/YYYY HH:mm"
              value-type="format"
              :lang="ptBR"
              :clearable="true"
              :show-second="false"
              :minute-step="5"
              placeholder="Selecione data e hora"
              class="w-full"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">Prioridade</label>
            <select
              v-model="form.priority"
              class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-2 text-n-slate-12"
            >
              <option value="none">Sem prioridade</option>
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">Status</label>
            <select
              v-model="form.status_key"
              class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-2 text-n-slate-12"
            >
              <option value="pending">Pendente</option>
              <option value="in_progress">Em andamento</option>
              <option value="done">Concluída</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">Atribuído a</label>
            <select
              v-model="form.assigned_to_user_id"
              class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-solid-2 text-n-slate-12"
            >
              <option :value="null">-- nenhum --</option>
              <option v-for="u in agents" :key="u.id" :value="u.id">
                {{ u.name }}
              </option>
            </select>
          </div>
        </div>
        <div
          class="flex justify-between items-center pt-4 border-t border-n-weak"
        >
          <button
            v-if="editingTask"
            type="button"
            class="text-sm text-red-600 hover:text-red-700"
            @click="deleteTask"
          >
            Excluir
          </button>
          <span v-else />
          <div class="flex gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-sm"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-50"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </div>
      </form>
    </woot-modal>
  </div>
</template>

<style>
.tasks-app select,
.tasks-app input[type='text'],
.kanban-tasks-tab select,
.kanban-tasks-tab input[type='text'] {
  background-position: right 12px center !important;
  padding-right: 32px !important;
}

/* DatePicker popup precisa ficar acima do modal-mask (z-index 9990) */
.mx-datepicker-popup {
  z-index: 9995 !important;
}

.mx-datepicker-main {
  z-index: 9995 !important;
}
</style>
